import React from 'react';
import styled from 'styled-components';
import { FaDownload, FaTint, FaWind, FaShower } from 'react-icons/fa';
import { PageHero, PageHeroInner, PageWrap, PageInner } from '../components/PageHero';

// ── SECTION HEADER ─────────────────────────────────────────
const SectionHeader = styled.div`
  padding: 2rem 2.5rem;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 1.5rem;

  span {
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: #00b4d8;
  }

  h2 {
    font-size: 1.3rem;
    font-weight: 900;
    color: #03045e;
    letter-spacing: -0.5px;
  }

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #f0f0f0;
  }
`;

// ── THERAPY TYPES ──────────────────────────────────────────
const TherapySection = styled.div`
  background: white;
  border: 1px solid #eee;
  margin-bottom: 2px;
`;

const TherapyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: #f0f0f0;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const TherapyCard = styled.div`
  background: white;
  padding: 3rem 2.5rem;
  transition: all 0.25s ease;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0;
    width: 0; height: 3px;
    background: #00b4d8;
    transition: width 0.35s ease;
  }

  &:hover {
    background: #03045e;
    &::after { width: 100%; }
    .icon { background: #00b4d8; color: #03045e; }
    h4, p, .num { color: white; }
    p { color: rgba(255,255,255,0.55); }
    .num { color: rgba(255,255,255,0.04); }
  }

  .num {
    font-size: 5rem;
    font-weight: 900;
    color: #f5f5f5;
    letter-spacing: -4px;
    line-height: 1;
    margin-bottom: 1.5rem;
    transition: color 0.25s ease;
  }

  .icon {
    width: 48px; height: 48px;
    background: #03045e;
    display: flex; align-items: center; justify-content: center;
    color: #00b4d8;
    font-size: 1.1rem;
    margin-bottom: 1.2rem;
    transition: all 0.25s ease;
  }

  h4 {
    font-size: 1.1rem;
    font-weight: 800;
    color: #03045e;
    margin-bottom: 0.8rem;
    letter-spacing: -0.3px;
    transition: color 0.25s ease;
  }

  p {
    font-size: 0.88rem;
    color: #777;
    line-height: 1.8;
    transition: color 0.25s ease;
  }
`;

// ── STATS ──────────────────────────────────────────────────
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  background: #eee;
  margin-bottom: 2px;

  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const StatBox = styled.div`
  background: #03045e;
  padding: 2.5rem;
  text-align: center;
  transition: background 0.25s ease;

  &:hover { background: #00b4d8; h3, p { color: #03045e; } }

  h3 {
    font-size: clamp(2rem, 3vw, 2.8rem);
    font-weight: 900;
    color: #00b4d8;
    letter-spacing: -2px;
    line-height: 1;
    transition: color 0.25s ease;
  }

  p {
    color: rgba(255,255,255,0.5);
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-top: 0.5rem;
    transition: color 0.25s ease;
  }
`;

// ── VIDEOS ─────────────────────────────────────────────────
const VideoSection = styled.div`
  background: white;
  border: 1px solid #eee;
  margin-bottom: 2px;
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: #f0f0f0;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const VideoItem = styled.div`
  background: white;
`;

const VideoWrap = styled.div`
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;

  iframe {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    border: 0;
  }
`;

const VideoLabel = styled.div`
  padding: 1.2rem 1.5rem;
  border-top: 2px solid #03045e;
  display: flex;
  align-items: center;
  justify-content: space-between;

  h4 {
    font-size: 0.88rem;
    font-weight: 700;
    color: #03045e;
  }

  p {
    font-size: 0.72rem;
    color: #aaa;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-top: 0.2rem;
  }

  span {
    font-size: 2rem;
    font-weight: 900;
    color: #f0f0f0;
    letter-spacing: -2px;
  }
`;

// ── SCIENCE ────────────────────────────────────────────────
const ScienceSection = styled.div`
  background: #03045e;
  border: 1px solid #03045e;
  margin-bottom: 2px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const ScienceBox = styled.div`
  background: #03045e;
  padding: 3rem 2.5rem;

  h3 {
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: #00b4d8;
    margin-bottom: 1.5rem;
  }

  ul {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 1rem;

    li {
      display: flex;
      align-items: flex-start;
      gap: 1rem;
      color: rgba(255,255,255,0.65);
      font-size: 0.92rem;
      line-height: 1.7;

      &::before {
        content: '→';
        color: #00b4d8;
        font-weight: 700;
        flex-shrink: 0;
      }
    }
  }
`;

const BigStat = styled.div`
  background: #00b4d8;
  padding: 3rem 2.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;

  h2 {
    font-size: clamp(4rem, 8vw, 7rem);
    font-weight: 900;
    color: #03045e;
    letter-spacing: -5px;
    line-height: 0.9;
  }

  p {
    color: rgba(3,4,94,0.65);
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-top: 1rem;
  }
`;

// ── DOWNLOAD ───────────────────────────────────────────────
const DownloadSection = styled.div`
  background: #03045e;
  border: 1px solid #03045e;
  padding: 3rem 2.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;

  @media (max-width: 768px) { flex-direction: column; text-align: center; }

  div {
    h3 {
      font-size: 0.72rem;
      font-weight: 800;
      letter-spacing: 3px;
      text-transform: uppercase;
      color: #00b4d8;
      margin-bottom: 0.8rem;
    }
    p {
      color: rgba(255,255,255,0.55);
      font-size: 0.95rem;
      line-height: 1.7;
    }
  }
`;

const DownloadBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 32px;
  background: #00b4d8;
  color: #03045e;
  font-size: 0.9rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.25s ease;

  &:hover { background: #90e0ef; }
`;

const videos = [
  { src: 'https://www.youtube.com/embed/bsTouxX3-P4', title: 'Hidrogen untuk Kesehatan', sub: 'Anti Oksidan Terbaik' },
  { src: 'https://www.youtube.com/embed/HVE6bH78Cm8', title: 'Uji Klinis Hidrogen', sub: 'Penelitian Ilmiah' },
  { src: 'https://www.youtube.com/embed/71PQn5-UniQ', title: 'Bukti Nyata Hidrogen', sub: 'Testimoni & Riset' },
];

export default function HidroFit() {
  return (
    <>
      <PageHero data-title="FIT">
        <PageHeroInner>
          <div className="left">
            <span>Hidrogen untuk Kesehatan</span>
            <h1 style={{ whiteSpace: 'nowrap', fontSize: 'clamp(2rem, 4.5vw, 5rem)' }}>hidrofit<sup style={{fontSize:'0.5em', verticalAlign:'super'}}>+</sup></h1>
            <p>Inovasi hidrogen untuk mendukung kesehatan tubuh, sterilisasi, dan pengolahan air bersih</p>
          </div>
          <div className="right">
            <div className="data-title">FIT</div>
          </div>
        </PageHeroInner>
      </PageHero>

      <PageWrap style={{ padding: '3rem 4rem' }}>
        <PageInner style={{ maxWidth: '1100px' }}>

          {/* THERAPY TYPES */}
          <TherapySection>
            <SectionHeader>
              <span>Metode Terapi</span>
              <h2>Tiga Cara Terapi Hidrogen</h2>
            </SectionHeader>
            <TherapyGrid>
              <TherapyCard>
                <div className="num">01</div>
                <div className="icon"><FaWind /></div>
                <h4>Terapi Uap</h4>
                <p>Menghirup uap hidrogen langsung untuk kesehatan saluran pernapasan dan regenerasi sel dari dalam.</p>
              </TherapyCard>
              <TherapyCard>
                <div className="num">02</div>
                <div className="icon"><FaShower /></div>
                <h4>Terapi Mandi</h4>
                <p>Bilasan mandi terakhir dengan air hidrogen untuk regenerasi kulit, detox penuaan, dan kesehatan rambut.</p>
              </TherapyCard>
              <TherapyCard>
                <div className="num">03</div>
                <div className="icon"><FaTint /></div>
                <h4>Terapi Minum</h4>
                <p>Air hidrogen sebagai anti oksidan terbaik — menyembuhkan 170+ penyakit, didukung jurnal kedokteran dunia.</p>
              </TherapyCard>
            </TherapyGrid>
          </TherapySection>

          {/* STATS */}
          <StatsRow>
            <StatBox><h3>170+</h3><p>Penyakit Teratasi</p></StatBox>
            <StatBox><h3>50+</h3><p>Tahun Penelitian</p></StatBox>
            <StatBox><h3>1,000+</h3><p>Jurnal Ilmiah</p></StatBox>
          </StatsRow>

          {/* VIDEOS */}
          <VideoSection>
            <SectionHeader>
              <span>Bukti Ilmiah</span>
              <h2>Video Penelitian & Testimoni</h2>
            </SectionHeader>
            <VideoGrid>
              {videos.map((v, i) => (
                <VideoItem key={i}>
                  <VideoWrap>
                    <iframe
                      title={v.title}
                      src={v.src}
                      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen loading="lazy"
                    />
                  </VideoWrap>
                  <VideoLabel>
                    <div>
                      <h4>{v.title}</h4>
                      <p>{v.sub}</p>
                    </div>
                    <span>0{i + 1}</span>
                  </VideoLabel>
                </VideoItem>
              ))}
            </VideoGrid>
          </VideoSection>

          {/* SCIENCE */}
          <ScienceSection>
            <ScienceBox>
              <h3>Mengapa Hidrogen?</h3>
              <ul>
                <li>Anti oksidan terkecil — menembus sel lebih cepat dari vitamin</li>
                <li>Selektif melawan radikal bebas berbahaya saja</li>
                <li>Toksisitas rendah, aman dikonsumsi seumur hidup</li>
                <li>Didukung 1,600+ peneliti dan 1,000+ jurnal ilmiah dunia</li>
                <li>Terbukti aman untuk semua kalangan lebih dari 50 tahun</li>
              </ul>
            </ScienceBox>
            <BigStat>
              <h2>170+</h2>
              <p>Penyakit Dapat Disembuhkan</p>
            </BigStat>
          </ScienceSection>

          {/* WTP */}
          <VideoSection>
            <SectionHeader>
              <span>Water Treatment Plant</span>
              <h2>Pengolahan Air Berbasis Hidrogen</h2>
            </SectionHeader>
            <VideoGrid style={{ gridTemplateColumns: '1fr' }}>
              <VideoItem>
                <VideoWrap>
                  <iframe
                    title="Water Treatment Plant"
                    src="https://www.youtube.com/embed/_VG60lZFEgk"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen loading="lazy"
                  />
                </VideoWrap>
                <VideoLabel>
                  <div>
                    <h4>Water Treatment Plant (WTP)</h4>
                    <p>Mengubah Air Laut TDS 5500 Menjadi Air Minum TDS Max 5</p>
                  </div>
                  <span>WTP</span>
                </VideoLabel>
              </VideoItem>
            </VideoGrid>
          </VideoSection>

          {/* DOWNLOAD */}
          <DownloadSection>
            <div>
              <h3>Unduh Brosur</h3>
              <p>Dapatkan informasi lengkap teknologi hidrofit⁺ dalam format PDF.</p>
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', flexShrink: 0 }}>
              <DownloadBtn href="/assets/media/BROSUR HIDROFITPLUS.pdf" download="BROSUR HIDROFITPLUS.pdf">
                <FaDownload size={14} /> Download Brosur hidrofit⁺
              </DownloadBtn>
              <DownloadBtn href="/assets/media/WTP HIDROFITPLUS.pdf" download="WTP HIDROFITPLUS.pdf">
                <FaDownload size={14} /> Download WTP
              </DownloadBtn>
            </div>
          </DownloadSection>

        </PageInner>
      </PageWrap>
    </>
  );
}
